/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import { BrandHealthCharts } from './BrandHealthCharts';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.css';

interface Demographics {
  age_segments?: any[];
  city_segments?: any[];
}

interface RagSource {
  title: string;
  content?: string;
  source?: string;
  page?: number;
  relevance: number;
}

interface EnhancedMessageProps {
  content: string;
  metrics?: any;
  competitors?: any[];
  demographics?: Demographics | any[];
  followUpQuestions?: string[];
  onFollowUpClick?: (question: string) => void;
  ragSources?: RagSource[];
  showCharts?: boolean;  // From backend - determines if charts are relevant
}

export const EnhancedMessage: React.FC<EnhancedMessageProps> = ({
  content,
  metrics,
  competitors,
  demographics,
  followUpQuestions,
  onFollowUpClick,
  ragSources,
  showCharts: initialShowCharts = true
}) => {
  const [chartsVisible, setChartsVisible] = useState(initialShowCharts);
  const [showSources, setShowSources] = useState(false);

  // Parse content to detect sections
  const sections = content.split(/^#{2,3}\s+/m).filter(Boolean);
  
  // Custom renderers for better formatting
  const components = {
    h2: ({ children }: any) => (
      <h2 className="text-xl font-bold text-primary mt-4 mb-2 flex items-center">
        <i className="fas fa-chart-line mr-2 text-primary"></i>
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-lg font-semibold text-cyan-600 dark:text-cyan-400 mt-3 mb-2">{children}</h3>
    ),
    ul: ({ children }: any) => (
      <ul className="space-y-2 ml-4">{children}</ul>
    ),
    li: ({ children }: any) => (
      <li className="flex items-start">
        <i className="fas fa-chevron-right text-primary mt-1 mr-2 text-xs"></i>
        <span className="text-text-secondary">{children}</span>
      </li>
    ),
    strong: ({ children }: any) => (
      <strong className="text-foreground font-semibold">{children}</strong>
    ),
    p: ({ children }: any) => (
      <p className="text-text-secondary leading-relaxed mb-2">{children}</p>
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-primary pl-4 my-3 italic text-text-muted">
        {children}
      </blockquote>
    )
  };

  // Render metric badges with accessible colors (high contrast)
  const renderMetricBadges = () => {
    if (!metrics) return null;

    // Helper to get badge styling based on value thresholds
    const getBadgeStyle = (value: number, goodThreshold: number, okThreshold: number) => {
      if (value > goodThreshold) {
        return 'bg-emerald-600 text-white'; // Green background, white text
      } else if (value > okThreshold) {
        return 'bg-amber-500 text-gray-900'; // Amber background, dark text
      } else {
        return 'bg-red-600 text-white'; // Red background, white text
      }
    };

    return (
      <div className="flex flex-wrap gap-2 mb-4">
        {metrics.awareness !== undefined && (
          <div className={`px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
            getBadgeStyle(metrics.awareness, 60, 30)
          }`}>
            <i className="fas fa-eye mr-1.5"></i>
            Awareness: {metrics.awareness.toFixed(0)}%
          </div>
        )}
        {metrics.trust !== undefined && (
          <div className={`px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
            getBadgeStyle(metrics.trust, 50, 30)
          }`}>
            <i className="fas fa-shield-alt mr-1.5"></i>
            Trust: {metrics.trust.toFixed(0)}%
          </div>
        )}
        {metrics.nps !== undefined && (
          <div className={`px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
            metrics.nps > 50 ? 'bg-emerald-600 text-white' :
            metrics.nps > 0 ? 'bg-amber-500 text-gray-900' :
            'bg-red-600 text-white'
          }`}>
            <i className="fas fa-star mr-1.5"></i>
            NPS: {metrics.nps}
          </div>
        )}
        {metrics.loyalty !== undefined && (
          <div className={`px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
            getBadgeStyle(metrics.loyalty, 40, 20)
          }`}>
            <i className="fas fa-heart mr-1.5"></i>
            Loyalty: {metrics.loyalty.toFixed(0)}%
          </div>
        )}
      </div>
    );
  };

  // Render RAG sources with page numbers
  const renderSources = () => {
    if (!ragSources || ragSources.length === 0) return null;

    return (
      <div className="mt-4 border-t border-border pt-4">
        <button
          onClick={() => setShowSources(!showSources)}
          className="text-primary hover:text-primary/80 text-sm font-medium mb-2"
        >
          <i className={`fas fa-${showSources ? 'chevron-down' : 'chevron-right'} mr-1`}></i>
          Sources & References ({ragSources.length})
        </button>

        {showSources && (
          <div className="space-y-2 mt-2">
            {ragSources.map((source, i) => (
              <div key={i} className="bg-background-tertiary rounded p-3">
                <div className="flex items-start justify-between mb-1">
                  <h5 className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
                    <i className="fas fa-book mr-1"></i>
                    {source.title}
                    {source.page && (
                      <span className="ml-1 text-text-muted font-normal">
                        (p. {source.page})
                      </span>
                    )}
                  </h5>
                  <span className="text-xs text-text-muted">
                    {(source.relevance * 100).toFixed(0)}% relevant
                  </span>
                </div>
                {source.content && (
                  <p className="text-xs text-text-muted line-clamp-2">{source.content}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render follow-up questions
  const renderFollowUpQuestions = () => {
    if (!followUpQuestions || followUpQuestions.length === 0) return null;

    return (
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-sm text-text-muted mb-2">Suggested follow-up questions:</p>
        <div className="flex flex-wrap gap-2">
          {followUpQuestions.map((question, i) => (
            <button
              key={i}
              onClick={() => onFollowUpClick?.(question)}
              className="px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary
                rounded-lg text-sm transition-colors border border-primary/30"
            >
              <i className="fas fa-robot mr-1"></i>
              {question}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Metric badges */}
      {renderMetricBadges()}

      {/* Main content with enhanced markdown */}
      <div className="prose dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex, rehypeHighlight]}
          components={components}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* RAG Sources */}
      {renderSources()}

      {/* Follow-up questions */}
      {renderFollowUpQuestions()}

      {/* Charts toggle - only show if backend says charts are relevant */}
      {initialShowCharts && (metrics || competitors || demographics) && (
        <button
          onClick={() => setChartsVisible(!chartsVisible)}
          className="text-primary hover:text-primary/80 text-sm font-medium mt-4"
        >
          <i className={`fas fa-chart-bar mr-1`}></i>
          {chartsVisible ? 'Hide' : 'Show'} Visualizations
        </button>
      )}

      {/* Charts - conditionally rendered based on backend flag and user toggle */}
      {initialShowCharts && chartsVisible && (
        <BrandHealthCharts
          metrics={metrics}
          competitors={competitors}
          demographics={demographics}
        />
      )}
    </div>
  );
};